import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, Checkbox,
  Container, FormControlLabel, MenuItem, Paper, Radio,
  RadioGroup, Stack, TextField, Typography
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient.ts';

type Result = {
  application_id: string;
  breed_decision: string;
};

type Slot = {
  slot_id: string;
  starts_at: string;
  ends_at: string;
};

const OTHER = 'Ander ras / niet gevonden';

export const NewDogApplicationPage = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [breeds, setBreeds] = useState<string[]>([]);

  const [name, setName] = useState('');
  const [dogType, setDogType] =
    useState<'pedigree' | 'crossbreed'>('pedigree');

  const [breed1, setBreed1] = useState<string | null>(null);
  const [breed2, setBreed2] = useState<string | null>(null);
  const [other1, setOther1] = useState('');
  const [other2, setOther2] = useState('');
  const [unknown, setUnknown] = useState(false);

  const [sex, setSex] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [neutered, setNeutered] = useState(false);
  const [chemical, setChemical] = useState(false);
  const [notes, setNotes] = useState('');

  const [result, setResult] = useState<Result | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotOffset, setSlotOffset] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [booked, setBooked] = useState<Slot | null>(null);

  useEffect(() => {
    async function load() {
      if (!supabase) return;

      const { data, error } = await supabase
        .from('dog_breeds')
        .select('breed_name')
        .eq('active', true)
        .order('sort_order')
        .order('breed_name');

      if (error) {
        setError('De rassenlijst kon niet worden geladen.');
        return;
      }

      setBreeds((data ?? []).map(x => x.breed_name));
    }

    load();
  }, []);

  const dateTime = (value: string) =>
    new Intl.DateTimeFormat('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(value));

  const time = (value: string) =>
    new Intl.DateTimeFormat('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(value));

  const breedValue = (
    selected: string | null,
    other: string
  ) =>
    selected === OTHER
      ? other.trim()
        ? `Ander ras: ${other.trim()}`
        : OTHER
      : selected?.trim() ?? '';

  async function loadSlots(offset: number) {
    if (!supabase) return;

    setBusy(true);
    setError('');
    setSelectedSlot('');

    const { data, error } = await supabase.rpc(
      'next_phone_call_slots',
      {
        p_limit: 3,
        p_offset: offset
      }
    );

    if (error) {
      setError('De beschikbare belmomenten konden niet worden geladen.');
      setBusy(false);
      return;
    }

    setSlots((data ?? []) as Slot[]);
    setSlotOffset(offset);
    setBusy(false);
  }

  async function submit() {
    if (!supabase) return;

    setError('');

    if (!name.trim() || !sex) {
      setError('Vul de naam en het geslacht van je hond in.');
      return;
    }

    if (!unknown && !breed1) {
      setError('Kies het ras van je hond.');
      return;
    }

    if (breed1 === OTHER && !other1.trim()) {
      setError('Vul het ras van je hond in.');
      return;
    }

    if (breed2 === OTHER && !other2.trim()) {
      setError('Vul het tweede ras van de kruising in.');
      return;
    }

    const selectedBreeds = unknown
      ? []
      : [
          breedValue(breed1, other1),
          breedValue(breed2, other2)
        ].filter(Boolean);

    setBusy(true);

    const { data, error } = await supabase.rpc(
      'create_customer_dog_application',
      {
        p_dog_name: name.trim(),
        p_is_crossbreed: dogType === 'crossbreed',
        p_breeds: selectedBreeds,
        p_crossbreed_unknown:
          dogType === 'crossbreed' && unknown,
        p_sex: sex,
        p_birth_date: birthDate || null,
        p_neutered: neutered,
        p_chemically_neutered: chemical,
        p_care_notes: notes
      }
    );

    if (error || !data?.[0]) {
      setError(
        error?.message ||
          'De aanmelding kon niet worden opgeslagen.'
      );
      setBusy(false);
      return;
    }

    const application = data[0] as Result;
    setResult(application);

    if (application.breed_decision !== 'continue') {
      setStep(3);
      setBusy(false);
      return;
    }

    setBusy(false);
    setStep(2);
    await loadSlots(0);
  }

  async function book() {
    if (!supabase || !result || !selectedSlot) return;

    setBusy(true);
    setError('');

    const { data, error } = await supabase.rpc(
      'book_customer_phone_call',
      {
        p_application_id: result.application_id,
        p_slot_id: selectedSlot
      }
    );

    if (error || !data?.[0]) {
      setError(
        error?.message ||
          'Het belmoment kon niet worden vastgelegd.'
      );
      setBusy(false);
      return;
    }

    setBooked({
      slot_id: data[0].slot_id,
      starts_at: data[0].starts_at,
      ends_at: data[0].ends_at
    });

    setStep(3);
    setBusy(false);
  }

  const BreedField = ({
    label,
    value,
    setValue,
    other,
    setOther
  }: {
    label: string;
    value: string | null;
    setValue: (v: string | null) => void;
    other: string;
    setOther: (v: string) => void;
  }) => (
    <Stack spacing={1}>
      <Autocomplete
        options={breeds}
        value={value}
        onChange={(_, v) => setValue(v)}
        autoHighlight
        openOnFocus={false}
        filterOptions={(options, state) => {
          const q = state.inputValue.trim().toLowerCase();
          if (!q) return options;

          return options.filter(x =>
            x.toLowerCase().includes(q)
          );
        }}
        noOptionsText="Geen ras gevonden"
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            placeholder="Typ om een ras te zoeken"
          />
        )}
      />

      {value === OTHER && (
        <TextField
          label="Welk ras?"
          value={other}
          onChange={e => setOther(e.target.value)}
          helperText="Pippaya bekijkt deze aanmelding persoonlijk."
        />
      )}
    </Stack>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fffafb', py: 4 }}>
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate('/mijn-pippaya/honden')}
        >
          Terug naar Mijn honden
        </Button>

        <Typography variant="overline" color="text.secondary">
          Mijn Pippaya
        </Typography>

        <Typography variant="h3">
          Nieuwe hond aanmelden
        </Typography>

        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Iedere nieuwe hond wordt eerst door Pippaya beoordeeld
          en maakt kennis voordat reserveren mogelijk is.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {step === 1 && (
          <Card sx={{ p: 3, mt: 3 }}>
            <Typography variant="h5">
              Gegevens van je hond
            </Typography>

            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                label="Naam hond"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />

              <Box>
                <Typography fontWeight={700}>
                  Is je hond een rashond of kruising?
                </Typography>

                <RadioGroup
                  row
                  value={dogType}
                  onChange={e => {
                    const type = e.target.value as
                      'pedigree' | 'crossbreed';

                    setDogType(type);
                    setBreed1(null);
                    setBreed2(null);
                    setOther1('');
                    setOther2('');
                    setUnknown(false);
                  }}
                >
                  <FormControlLabel
                    value="pedigree"
                    control={<Radio />}
                    label="Rashond"
                  />
                  <FormControlLabel
                    value="crossbreed"
                    control={<Radio />}
                    label="Kruising"
                  />
                </RadioGroup>
              </Box>

              {dogType === 'crossbreed' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={unknown}
                      onChange={e => {
                        setUnknown(e.target.checked);

                        if (e.target.checked) {
                          setBreed1(null);
                          setBreed2(null);
                          setOther1('');
                          setOther2('');
                        }
                      }}
                    />
                  }
                  label="De afkomst/rassen van de kruising zijn onbekend"
                />
              )}

              {!unknown && (
                <>
                  <BreedField
                    label={
                      dogType === 'pedigree'
                        ? 'Ras'
                        : 'Eerste ras'
                    }
                    value={breed1}
                    setValue={setBreed1}
                    other={other1}
                    setOther={setOther1}
                  />

                  {dogType === 'crossbreed' && (
                    <BreedField
                      label="Tweede ras (indien bekend)"
                      value={breed2}
                      setValue={setBreed2}
                      other={other2}
                      setOther={setOther2}
                    />
                  )}
                </>
              )}

              <TextField
                select
                label="Geslacht"
                value={sex}
                onChange={e => setSex(e.target.value)}
                required
              >
                <MenuItem value="teef">Teef</MenuItem>
                <MenuItem value="reu">Reu</MenuItem>
              </TextField>

              <TextField
                type="date"
                label="Geboortedatum"
                value={birthDate}
                onChange={e => setBirthDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().slice(0, 10)
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={neutered}
                    onChange={e => setNeutered(e.target.checked)}
                  />
                }
                label="Gecastreerd / gesteriliseerd"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={chemical}
                    onChange={e => setChemical(e.target.checked)}
                  />
                }
                label="Chemisch gecastreerd"
              />

              <TextField
                label="Verzorging & bijzonderheden"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                multiline
                minRows={4}
                helperText="Bijvoorbeeld voeding, medicatie of andere belangrijke aandachtspunten voor de opvang."
              />

              <Button
                variant="contained"
                size="large"
                disabled={busy}
                onClick={submit}
              >
                {busy ? 'Even geduld...' : 'Verder met aanmelding'}
              </Button>
            </Stack>
          </Card>
        )}

        {step === 2 && result && (
          <Card sx={{ p: 3, mt: 3 }}>
            <Alert severity="info">
              <Typography fontWeight={800}>
                Telefonisch kennismaken
              </Typography>
              Voordat we een kennismaking met je hond plannen,
              maken we graag eerst telefonisch kennis.
            </Alert>

            <Typography variant="h5" sx={{ mt: 3 }}>
              Kies een belmoment
            </Typography>

            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Kies één van de onderstaande mogelijkheden.
            </Typography>

            {slots.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                Er zijn geen verdere vrije belmomenten beschikbaar.
                Pippaya neemt contact met je op.
              </Alert>
            ) : (
              <RadioGroup
                value={selectedSlot}
                onChange={e => setSelectedSlot(e.target.value)}
                sx={{ mt: 2 }}
              >
                {slots.map(slot => (
                  <Paper
                    key={slot.slot_id}
                    variant="outlined"
                    sx={{ p: 1.5, mb: 1 }}
                  >
                    <FormControlLabel
                      value={slot.slot_id}
                      control={<Radio />}
                      label={`${dateTime(slot.starts_at)} – ${time(
                        slot.ends_at
                      )}`}
                    />
                  </Paper>
                ))}
              </RadioGroup>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ mt: 2 }}
            >
              {slotOffset > 0 && (
                <Button
                  variant="outlined"
                  disabled={busy}
                  onClick={() =>
                    loadSlots(Math.max(0, slotOffset - 3))
                  }
                >
                  Vorige momenten
                </Button>
              )}

              {slots.length === 3 && (
                <Button
                  variant="outlined"
                  disabled={busy}
                  onClick={() => loadSlots(slotOffset + 3)}
                >
                  Andere momenten bekijken
                </Button>
              )}
            </Stack>

            {slots.length > 0 && (
              <Button
                variant="contained"
                size="large"
                disabled={!selectedSlot || busy}
                onClick={book}
                sx={{ mt: 2 }}
              >
                {busy ? 'Vastleggen...' : 'Belmoment vastleggen'}
              </Button>
            )}
          </Card>
        )}

        {step === 3 && result && (
          <Card sx={{ p: 3, mt: 3 }}>
            {result.breed_decision === 'excluded' && (
              <Alert severity="info">
                <Typography fontWeight={800}>
                  Bedankt voor je aanvraag
                </Typography>
                <Typography sx={{ mt: 1 }}>
                  Helaas kunnen wij voor deze hond geen passende
                  opvang aanbieden.
                </Typography>
              </Alert>
            )}

            {result.breed_decision === 'review' && (
              <Alert severity="info">
                <Typography fontWeight={800}>
                  Bedankt voor je aanmelding
                </Typography>
                <Typography sx={{ mt: 1 }}>
                  We bekijken de gegevens van je hond persoonlijk.
                  Pippaya neemt daarna contact met je op.
                </Typography>
              </Alert>
            )}

            {result.breed_decision === 'continue' && booked && (
              <Alert severity="success">
                <Typography fontWeight={800}>
                  Je telefonische kennismaking staat ingepland
                </Typography>

                <Typography sx={{ mt: 1 }}>
                  We bellen je op{' '}
                  <strong>{dateTime(booked.starts_at)}</strong>.
                </Typography>

                <Typography sx={{ mt: 1 }}>
                  Tijdens het telefoongesprek bespreken we je hond
                  en de opvang. Daarna bekijken we of we een
                  persoonlijke kennismaking inplannen. Pas na een
                  geslaagde kennismaking kan er opvang worden
                  gereserveerd.
                </Typography>
              </Alert>
            )}

            <Button
              variant="contained"
              sx={{ mt: 3 }}
              onClick={() => navigate('/mijn-pippaya/honden')}
            >
              Terug naar Mijn honden
            </Button>
          </Card>
        )}
      </Container>
    </Box>
  );
};